import chinobiAvatar from '@/assets/images/agents/chinobi.png';
import type {
  AdMetricItem,
  ConsultationStep,
  OperationCard,
} from '@/types/meet-live-ai';

const CHINOBI_AVATAR = chinobiAvatar;
const OPERATION_TEAM_AVATARS = [
  '/agents/thumbnails/Scyra.webp',
  '/agents/thumbnails/Mariana.webp',
];

export const OPERATION_CARDS: OperationCard[] = [
  {
    id: 'underwriting',
    number: '01',
    title: 'Underwriting Excellence',
    description:
      'Reduce risk, cut losses, and accelerate approvals with AI-grounded insight on every file.',
    category: 'Risk & Decisioning',
    iconType: 'underwriting',
    leadAvatar: CHINOBI_AVATAR,
    teamAvatars: OPERATION_TEAM_AVATARS,
  },
  {
    id: 'recovery',
    number: '02',
    title: 'Recovery Operations',
    description:
      'Improve recovery performance, prioritize smarter actions, and support more effective collections and post-funding follow-through.',
    category: 'Collections & Retention',
    iconType: 'recovery',
    leadAvatar: CHINOBI_AVATAR,
    teamAvatars: OPERATION_TEAM_AVATARS,
  },
  {
    id: 'origination',
    number: '03',
    title: 'Origination Operations',
    description:
      'Streamline intake, improve lead handling, and help teams move opportunities forward with more speed, structure, and consistency.',
    category: 'Intake & Conversion',
    iconType: 'origination',
    leadAvatar: CHINOBI_AVATAR,
    teamAvatars: OPERATION_TEAM_AVATARS,
  },
  {
    id: 'admin',
    number: '04',
    title: 'Administrative Coordination',
    description:
      'Reduce operational friction by supporting the administrative work that keeps teams organized, responsive, and moving efficiently.',
    category: 'Workflow & Orchestration',
    iconType: 'admin',
    leadAvatar: CHINOBI_AVATAR,
    teamAvatars: OPERATION_TEAM_AVATARS,
  },
  {
    id: 'enablement',
    number: '05',
    title: 'Operational Enablement',
    description:
      'Connect people, workflows, and AI support to reduce manual effort, improve execution, and unlock more capacity across the business.',
    category: 'Scale & Performance',
    iconType: 'enablement',
    leadAvatar: CHINOBI_AVATAR,
    teamAvatars: OPERATION_TEAM_AVATARS,
  },
];

export const CONSULTATION_STEPS: ConsultationStep[] = [
  {
    number: '01',
    title: 'Ask your questions in real time',
    description:
      'Speak directly with Chinobi in a live video session. No scripts, no slides—just a focused strategic conversation about your operations.',
    isActive: true,
  },
  {
    number: '02',
    title: 'Shared memory',
    description:
      "Chinobi draws on Agentous's full agent ecosystem to surface relevant approaches, operational patterns, and decision frameworks.",
    isActive: false,
  },
  {
    number: '03',
    title: 'Explore relevant agents and suites',
    description:
      "Learn which Agentous agents apply to your specific workflow—whether that's origination, underwriting, collections, or compliance.",
    isActive: false,
  },
  {
    number: '04',
    title: 'Schedule a demo to learn more',
    description:
      'Use your consultation to explore what matters most to your business. For a deeper look, Chinobi can help you schedule a demo.',
    isActive: false,
  },
];

export const AD_METRICS_DATA: AdMetricItem[] = [
  {
    id: 'throughput',
    label: 'Agent Case Throughput',
    value: '4.8x',
    change: '+380%',
    isPositive: true,
    benchmark: 'vs traditional manual intake',
    detail:
      'Average turnaround reduced from 72h to 14.2 minutes across 12,400+ commercial files.',
  },
  {
    id: 'decision-accuracy',
    label: 'Decision Precision Rate',
    value: '99.4%',
    change: '+14.2%',
    isPositive: true,
    benchmark: 'audited against post-fund metrics',
    detail:
      'Zero compliance drift across multi-jurisdictional underwriting models in 2026.',
  },
  {
    id: 'recovery-rate',
    label: 'Recovery Yield Optimization',
    value: '$18.4M',
    change: '+42.6%',
    isPositive: true,
    benchmark: 'annualized recovered capital',
    detail:
      'Automated early-intervention triage targeting high-propensity recovery accounts.',
  },
  {
    id: 'op-cost',
    label: 'Unit Processing Overhead',
    value: '-64%',
    change: '-$840k',
    isPositive: true,
    benchmark: 'cost reduction per 1,000 files',
    detail:
      'Eliminated redundant re-keying and fragmented hand-offs across core teams.',
  },
];
